import { ArrowUpRight, AlertTriangle, Sparkles } from 'lucide-react';

const LEAD_SCORE_COPY = {
    'Hot Lead': { label: 'HOT', tone: '#67E8F9' },
    'Warm Lead': { label: 'WARM', tone: '#FFFFFF' },
    'Nurture Lead': { label: 'NURTURE', tone: '#94A3B8' },
};

export function ResultScreen({ contact, recommendation, submitMeta }) {
    const {
        primary_system,
        secondary_system,
        main_bottleneck,
        explanation,
        next_steps,
        build_path,
        lead_score,
    } = recommendation;
    const score = LEAD_SCORE_COPY[lead_score] || LEAD_SCORE_COPY['Warm Lead'];

    return (
        <div data-testid="snapshot-result">
            {/* Top metadata strip */}
            <div
                className="flex items-center justify-between gap-3 pb-3 border-b font-mono text-[10px] uppercase tracking-[0.22em]"
                style={{ borderColor: 'rgba(255,255,255,0.10)' }}
            >
                <span className="text-slate-400">SNAPSHOT · COMPILED</span>
                <span
                    data-testid="snapshot-lead-score"
                    style={{ color: score.tone }}
                    className="font-semibold inline-flex items-center gap-1.5"
                >
                    <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: score.tone }} />
                    LEAD CLASSIFICATION · {score.label}
                </span>
            </div>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Prepared for {contact.name.split(' ')[0] || 'you'} ·{' '}
                {contact.business_name || 'business'}
            </p>
            <h2 className="mt-3 font-display font-semibold text-[2.1rem] md:text-[3.2rem] leading-[1.0] tracking-[-0.04em] text-white">
                Your{' '}
                <span className="text-gradient-cyan">Growth System</span>
                <br />
                Snapshot.
            </h2>

            {/* Recommendation block — primary card */}
            <div
                className="mt-8 p-6 md:p-8 rounded-2xl relative overflow-hidden"
                style={{
                    background: 'linear-gradient(160deg, rgba(6,182,212,0.10), rgba(11,18,33,0.5))',
                    border: '1px solid rgba(6,182,212,0.35)',
                    boxShadow: '0 30px 60px -28px rgba(6,182,212,0.4)',
                }}
            >
                <div
                    className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-50"
                    style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.4), transparent 65%)' }}
                />
                <p className="relative font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">
                    Recommended priority
                </p>
                <p
                    data-testid="snapshot-primary-system"
                    className="relative mt-2 font-display font-semibold text-[1.8rem] md:text-[2.4rem] leading-[1.1] tracking-[-0.035em] text-white"
                >
                    {primary_system}.
                </p>
                <p className="relative mt-4 text-[15.5px] leading-[1.65] text-slate-300 max-w-2xl">
                    {explanation}
                </p>
            </div>

            {/* Secondary + bottleneck row */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                    className="p-5 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        Secondary system
                    </p>
                    <p
                        data-testid="snapshot-secondary-system"
                        className="mt-2 font-display font-medium text-[19px] tracking-[-0.025em] text-white"
                    >
                        {secondary_system}
                    </p>
                </div>
                <div
                    className="p-5 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        Main bottleneck
                    </p>
                    <p className="mt-2 text-[14px] leading-[1.5] text-slate-300">
                        {main_bottleneck}
                    </p>
                </div>
            </div>

            {/* Five next steps */}
            <div className="mt-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">
                    Prescription
                </p>
                <p className="mt-3 font-display font-semibold text-[20px] md:text-[24px] leading-[1.25] tracking-[-0.03em] text-white max-w-2xl">
                    Here's what's causing the drop-off and what to fix first:
                </p>
                <ol className="mt-5 space-y-1">
                    {next_steps.map((s, i) => (
                        <li
                            key={s}
                            data-testid={`snapshot-next-step-${i}`}
                            className="flex items-start gap-4 py-3 border-t"
                            style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                        >
                            <span className="font-mono text-[11px] tracking-[0.16em] text-cyan-400 mt-0.5 shrink-0 w-7 tabular-nums">
                                {String(i + 1).padStart(2, '0')}
                            </span>
                            <span className="text-[15px] leading-[1.55] text-slate-200">{s}</span>
                        </li>
                    ))}
                </ol>
            </div>

            {/* Build path */}
            <div
                className="mt-8 p-6 rounded-2xl grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-center"
                style={{
                    background: 'linear-gradient(140deg, rgba(139,92,246,0.08), rgba(11,18,33,0.5))',
                    border: '1px solid rgba(139,92,246,0.30)',
                }}
            >
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-violet-300">
                        Recommended ClearAdvance build path
                    </p>
                    <p
                        data-testid="snapshot-build-path"
                        className="mt-2 font-display font-semibold text-[20px] md:text-[24px] tracking-[-0.025em] text-white"
                    >
                        {build_path}.
                    </p>
                </div>
                <a
                    href="mailto:hello@clearadvance.co?subject=Build%20a%20Growth%20System%20Like%20This"
                    data-testid="snapshot-build-cta"
                    className="btn btn-primary !text-[13px] !py-2.5 !px-4"
                >
                    <Sparkles className="w-3.5 h-3.5" />
                    Build this
                    <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
            </div>

            {submitMeta?.saved_failed && (
                <div
                    className="mt-6 p-3 rounded-lg flex items-start gap-2"
                    style={{
                        background: 'rgba(239,68,68,0.08)',
                        border: '1px solid rgba(239,68,68,0.30)',
                    }}
                >
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-300 shrink-0" />
                    <p className="text-[13px] text-slate-300">
                        Your snapshot is ready, but saving to our system didn't
                        complete. Screenshot this page or{' '}
                        <a
                            href="mailto:hello@clearadvance.co?subject=Growth%20System%20Snapshot"
                            className="underline text-cyan-400"
                        >
                            email us directly
                        </a>
                        .
                    </p>
                </div>
            )}

            {submitMeta?.id && (
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Snapshot saved · ID {submitMeta.id.slice(0, 8)}
                    {submitMeta.email_sent ? ' · notification sent' : ''}
                </p>
            )}
        </div>
    );
}
